/**
 * Brand-guide landing header with sticky navigation and responsive layout.
 *
 * What: Renders the landing page top bar with brand identity, anchor navigation, and CTAs with sticky scroll behavior.
 * Why: Provides persistent navigation access and clear entry points to key sections and authentication.
 * When: Update when changing landing section ids/labels, branding identity, or CTA behavior,
 * or when changing what `hideTrialCtas` suppresses for sales-free pages (e.g. partnership pages).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarMonth as CalendarIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import {
  landingNavConfig,
  landingNavItemTypes,
} from '@/components/landing/landingContent';
import LandingMegaMenu from '@/components/landing/LandingMegaMenu';
import { colors, radii, shadows } from '@/components/landing/landingTokens.js';
import ProUpsellDialog from '@/components/Practice/ProUpsellDialog';
import { getBrandingConfig } from '@/config/runtimeBrandConfig';
import { useTuitionSubject } from '@/contexts/tuitionSubjectContext';
import { useLeanBootCtaReplay } from '@/hooks/useLeanBootCtaReplay';
import { navigateToLandingSection } from '@/utils/landingSectionNavigation';

const HEADER_LOGO_FALLBACK_STYLE: React.CSSProperties = {
  display: 'block',
  height: '24px',
  width: 'auto',
  maxWidth: '160px',
};

interface LandingTopBarProps {
  onLogin: () => void;
  onBookTrial?: () => void;
  activeSection?: string;
  onOpenMobileNav?: () => void;
  /**
   * Hide every sales affordance (the "Book Free Trial" CTA and its lead-capture
   * dialog). For pages that are social good rather than acquisition, such as the
   * partnership welcome pages. Brand, nav, mega menu, hamburger and Login stay.
   */
  hideTrialCtas?: boolean;
}

function LandingTopBar({
  onLogin,
  onBookTrial,
  activeSection = '',
  onOpenMobileNav,
  hideTrialCtas = false,
}: LandingTopBarProps) {
  const tuition = useTuitionSubject();
  const SUBJECTS_MENU_ID = 'landing-subjects-menu';
  const SUBJECTS_TRIGGER_ID = 'landing-subjects-trigger';
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const branding = getBrandingConfig();
  const brandName = branding?.siteTitle || 'DeepThink';
  const logoUrl = branding?.logoUrl;
  const footerLogoUrl = branding?.footerLogoUrl;
  const headerLogoUrl = footerLogoUrl || logoUrl;
  const logoIncludesWordmark = Boolean(footerLogoUrl);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubjectsMenuOpen, setIsSubjectsMenuOpen] = useState(false);
  const subjectsTriggerRef = useRef<HTMLButtonElement>(null);
  const subjectsDropdownItem = landingNavConfig.desktop.find(
    item => item.type === landingNavItemTypes.dropdown && item.isDropdown
  );
  const subjectsMenuGroups = subjectsDropdownItem?.groups || [];

  // Re-click any CTA the visitor pressed before the app shell booted on a
  // lean marketing page. Lives in the shared marketing chrome so every
  // lean-boot route gets replay without extra wiring.
  useLeanBootCtaReplay();

  // Track scroll position for sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      const shouldBeScrolled = window.scrollY > 50;
      setIsScrolled(prev =>
        prev === shouldBeScrolled ? prev : shouldBeScrolled
      );
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeSubjectsMenu = () => {
    setIsSubjectsMenuOpen(false);
  };

  const openSubjectsMenu = () => {
    setIsSubjectsMenuOpen(true);
  };

  const handleSectionNavClick = (sectionId: string) => {
    closeSubjectsMenu();
    const isMobileViewport =
      typeof window !== 'undefined' &&
      window.innerWidth < theme.breakpoints.values.md;
    navigateToLandingSection({
      sectionId,
      pathname,
      navigate,
      isMobile: isMobileViewport,
    });
  };

  const handleBookTrialClick = () => {
    if (onBookTrial) {
      onBookTrial();
    } else {
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const isRouteNavItemActive = (routePath: string) =>
    pathname === routePath || pathname.startsWith(`${routePath}/`);

  const isSectionNavItemActive = (sectionId: string) =>
    pathname === '/' && activeSection === sectionId;

  const getDesktopNavItemSx = (isActive: boolean) => ({
    color: isActive ? colors.ink : colors.inkMuted,
    fontWeight: isActive ? 700 : 600,
    fontSize: '0.9375rem',
    textTransform: 'none' as const,
    px: 2,
    py: 1,
    borderRadius: radii.sm,
    position: 'relative' as const,
    overflow: 'visible',
    outline: '2px solid transparent',
    outlineOffset: 2,
    '&:focus-visible': {
      outline: `3px solid ${colors.accentCobalt}`,
      outlineOffset: 2,
    },
    '&:hover': {
      color: colors.ink,
      backgroundColor: colors.backgroundAlt,
    },
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      bottom: 0,
      left: '50%',
      transform: isActive
        ? 'translateX(-50%) scaleX(1)'
        : 'translateX(-50%) scaleX(0)',
      width: 'calc(100% - 16px)',
      height: 2,
      backgroundColor: colors.accentOrange,
      borderRadius: 1,
      transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&:hover::after': {
      transform: 'translateX(-50%) scaleX(1)',
    },
    transition: 'all 0.2s ease',
  });

  // Close any open desktop dropdown when route changes.
  useEffect(() => {
    closeSubjectsMenu();
  }, [pathname]);

  // Close the mega menu when users click outside trigger/menu.
  useEffect(() => {
    if (!isSubjectsMenuOpen) {
      return undefined;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) {
        return;
      }

      const menuElement = document.getElementById(SUBJECTS_MENU_ID);
      const isInsideTrigger =
        subjectsTriggerRef.current?.contains(event.target) ?? false;
      const isInsideMenu = menuElement?.contains(event.target) ?? false;

      if (isInsideTrigger || isInsideMenu) {
        return;
      }

      closeSubjectsMenu();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [isSubjectsMenuOpen]);

  return (
    <>
      <Box
        component="header"
        sx={{
          position: isScrolled ? 'fixed' : 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          // Provide breathing room for notches / safe areas
          pt: {
            xs: isScrolled
              ? 'calc(env(safe-area-inset-top) + 4px)'
              : 'calc(env(safe-area-inset-top) + 8px)',
            sm: isScrolled
              ? 'calc(env(safe-area-inset-top) + 6px)'
              : 'calc(env(safe-area-inset-top) + 12px)',
            md: isScrolled ? 1 : 2,
          },
          pb: { xs: isScrolled ? 0.75 : 1.5, sm: isScrolled ? 1 : 2 },
          pointerEvents: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: isScrolled
            ? 'rgba(255, 255, 255, 0.95)'
            : 'transparent',
          backdropFilter: isScrolled ? 'blur(8px)' : 'none',
          boxShadow: isScrolled ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pointerEvents: 'auto', position: 'relative' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 1.5, sm: 2, md: 3 },
              px: { xs: 2, sm: 2.5, md: 3 },
              py: {
                xs: isScrolled ? 0.625 : 0.75,
                sm: isScrolled ? 1.125 : 1.5,
                md: isScrolled ? 1.25 : 1.5,
              },
              minHeight: { xs: 44, sm: 48, md: 'auto' },
              borderRadius: { xs: radii.md, md: radii.lg },
              backgroundColor: colors.paper,
              border: `1px solid ${colors.border}`,
              boxShadow: isScrolled ? shadows.soft : shadows.soft,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* Logo */}
            <Stack
              component={Link}
              to="/"
              direction="row"
              spacing={1.25}
              alignItems="center"
              onClick={(e: React.MouseEvent) => {
                if (pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              sx={{
                minWidth: 0,
                textDecoration: 'none',
                color: colors.ink,
                outline: '2px solid transparent',
                outlineOffset: 3,
                cursor: 'pointer',
                '&:focus-visible': {
                  outline: `3px solid ${colors.accentCobalt}`,
                },
              }}
              aria-label={`${brandName} home`}
            >
              {headerLogoUrl ? (
                <Box
                  component="img"
                  src={headerLogoUrl}
                  alt={`${brandName} logo`}
                  aria-hidden="true"
                  {...{ fetchpriority: 'high' }}
                  width={160}
                  height={33}
                  style={HEADER_LOGO_FALLBACK_STYLE}
                  sx={{
                    height: { xs: 22, md: 26 },
                    width: 'auto',
                    display: 'block',
                    maxWidth: { xs: 140, sm: 160 },
                    transition: 'height 0.3s ease',
                  }}
                />
              ) : (
                <Box
                  aria-hidden="true"
                  sx={{
                    height: 12,
                    width: 12,
                    borderRadius: 999,
                    backgroundColor: colors.accentOrange,
                  }}
                />
              )}
              {!logoIncludesWordmark && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -0.2,
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {brandName}
                </Typography>
              )}
            </Stack>

            {/* Desktop Navigation Links - Center */}
            <Stack
              component="nav"
              direction="row"
              spacing={0.5}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flex: 1,
                justifyContent: 'center',
                mx: 2,
              }}
              aria-label="Main navigation"
            >
              {landingNavConfig.desktop.map(item => {
                if (
                  item.type === landingNavItemTypes.dropdown &&
                  item.isDropdown
                ) {
                  return (
                    <Button
                      key={`dropdown-${item.label}`}
                      id={SUBJECTS_TRIGGER_ID}
                      ref={subjectsTriggerRef}
                      onClick={openSubjectsMenu}
                      onMouseEnter={openSubjectsMenu}
                      data-boot-cta="nav-levels"
                      aria-haspopup="true"
                      aria-controls={SUBJECTS_MENU_ID}
                      aria-expanded={isSubjectsMenuOpen}
                      sx={getDesktopNavItemSx(isSubjectsMenuOpen)}
                    >
                      {item.label}
                    </Button>
                  );
                }

                if (item.type === landingNavItemTypes.section) {
                  const isActive = isSectionNavItemActive(item.id!);
                  return (
                    <Button
                      key={item.id}
                      onClick={() => handleSectionNavClick(item.id!)}
                      onMouseEnter={closeSubjectsMenu}
                      data-boot-cta={`nav-section-${item.id}`}
                      aria-current={isActive ? 'location' : undefined}
                      sx={getDesktopNavItemSx(isActive)}
                    >
                      {item.label}
                    </Button>
                  );
                }

                if (item.type === landingNavItemTypes.route) {
                  const isActive = isRouteNavItemActive(item.path!);
                  return (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path!}
                      onClick={closeSubjectsMenu}
                      onMouseEnter={closeSubjectsMenu}
                      aria-current={isActive ? 'page' : undefined}
                      sx={getDesktopNavItemSx(isActive)}
                    >
                      {item.label}
                    </Button>
                  );
                }

                return null;
              })}
            </Stack>

            {/* Right Side Actions */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Mobile: hamburger in-bar for alignment with nav bar */}
              {typeof onOpenMobileNav === 'function' && (
                <IconButton
                  onClick={onOpenMobileNav}
                  data-boot-cta="mobile-nav"
                  sx={{
                    display: { xs: 'inline-flex', md: 'none' },
                    backgroundColor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    borderRadius: radii.md,
                    width: 48,
                    height: 48,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    outline: '2px solid transparent',
                    outlineOffset: 2,
                    '&:focus-visible': {
                      outline: `3px solid ${colors.accentCobalt}`,
                      outlineOffset: 2,
                    },
                    '&:hover': {
                      backgroundColor: colors.backgroundAlt,
                    },
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  aria-label="Open navigation menu"
                  aria-haspopup="dialog"
                  aria-expanded={false}
                >
                  <MenuIcon sx={{ color: colors.ink, fontSize: 24 }} />
                </IconButton>
              )}
              {/* Book Trial CTA - Desktop Only */}
              {!hideTrialCtas && (
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleBookTrialClick}
                  startIcon={<CalendarIcon />}
                  data-boot-cta="trial"
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    whiteSpace: 'nowrap',
                    px: 2.5,
                    py: 1,
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: radii.md,
                    backgroundColor: colors.accentOrange,
                    color: colors.paper,
                    boxShadow: '0 4px 12px rgba(240, 104, 48, 0.2)',
                    outline: '2px solid transparent',
                    outlineOffset: 2,
                    fontSize: '0.9375rem',
                    '&:focus-visible': {
                      outline: `3px solid ${colors.accentCobalt}`,
                      outlineOffset: 2,
                    },
                    '&:hover': {
                      backgroundColor: colors.accentOrangeLight,
                      boxShadow: '0 6px 16px rgba(240, 104, 48, 0.3)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  aria-label={`Book a ${tuition.trial}`}
                >
                  {tuition.compactCta}
                </Button>
              )}

              {/* Student Login - Desktop Only (hidden on mobile, shown in drawer) */}
              <Button
                size="medium"
                variant="outlined"
                onClick={onLogin}
                data-boot-cta="login"
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  px: 2.25,
                  py: 1,
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: radii.md,
                  backgroundColor: 'transparent',
                  color: colors.ink,
                  border: `1.5px solid ${colors.border}`,
                  boxShadow: 'none',
                  outline: '2px solid transparent',
                  outlineOffset: 2,
                  fontSize: '0.9375rem',
                  '&:focus-visible': {
                    outline: `3px solid ${colors.accentCobalt}`,
                    outlineOffset: 2,
                  },
                  '&:hover': {
                    backgroundColor: colors.backgroundAlt,
                    borderColor: colors.inkSubtle,
                    boxShadow: 'none',
                  },
                  transition: 'all 0.2s ease',
                }}
                aria-label="Login for existing students and parents"
              >
                Login
              </Button>
            </Stack>
          </Box>

          {subjectsMenuGroups.length > 0 && (
            <LandingMegaMenu
              isOpen={isSubjectsMenuOpen}
              onClose={closeSubjectsMenu}
              onEscapeKeyDown={() => {
                closeSubjectsMenu();
                subjectsTriggerRef.current?.focus();
              }}
              menuId={SUBJECTS_MENU_ID}
              triggerId={SUBJECTS_TRIGGER_ID}
              groups={subjectsMenuGroups}
            />
          )}
        </Container>
      </Box>

      {/* Lead Capture Dialog */}
      {!hideTrialCtas && (
        <ProUpsellDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          skipOverview={true}
          sourceLocation="landing_topbar_desktop"
        />
      )}
    </>
  );
}

export default LandingTopBar;
